import { Check, ArrowRight } from 'lucide-react'
import { usePublicStats } from '@/hooks/usePublicStats'
import { TELEGRAM_BOT_URL, formatMoney } from '@/lib/constants'
import { cn } from '@/lib/utils'
import { useScrollReveal } from '@/hooks/useScrollReveal'

const PLAN_ORDER = ['FREE', 'PRO', 'ELITE', 'BLACK']

const PLAN_LABEL: Record<string, string> = { FREE: 'Free', PRO: 'Pro', ELITE: 'Elite', BLACK: 'Black' }

const PLAN_TAGLINE: Record<string, string> = {
  FREE: 'Para empezar',
  PRO: 'Mejor relación calidad/precio',
  ELITE: 'Más elegido',
  BLACK: 'Para usuarios avanzados',
}

const PLAN_FEATURES: Record<string, string[]> = {
  FREE: [
    '1 Top Pick al día',
    'Sin tarjeta de crédito',
    'Acceso a métricas públicas',
    'Historial de picks básico',
  ],
  PRO: [
    'Los 3 Top Picks del día',
    'Historial y rendimiento completo',
    'Estadísticas en tiempo real',
    'Sin publicidad',
  ],
  ELITE: [
    'Todo lo de Pro',
    'Parlays inteligentes incluidos',
    'Combinaciones automáticas de IA',
    'Cuotas combinadas calculadas',
  ],
  BLACK: [
    'Todo lo de Elite',
    'Bankroll y gestión de banca',
    'Acceso prioritario a novedades',
  ],
}

export function PlansSection() {
  const { data } = usePublicStats()
  const freeTrialDays = data?.free_trial_days ?? 5
  const ref = useScrollReveal<HTMLElement>()

  return (
    <section ref={ref} id="planes" className="bg-panel/40 border-y border-border">
      <div className="max-w-5xl mx-auto px-5 py-20">
        <div className="text-center mb-12 reveal">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand mb-2">
            Planes
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold">
            Empieza gratis, sube cuando quieras
          </h2>
          <p className="mt-3 text-text-dim max-w-lg mx-auto text-sm">
            Sin compromisos. Cancela cuando quieras desde la app.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PLAN_ORDER.map((plan, i) => {
            const price = data?.plans[plan] ?? 0
            const isPopular = plan === 'ELITE'
            return (
              <div
                key={plan}
                className={cn(
                  'reveal card flex flex-col transition-all duration-300',
                  isPopular && 'border-brand/60 relative shadow-brand-sm',
                )}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                {isPopular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full bg-brand text-black text-[10px] font-bold uppercase tracking-wide whitespace-nowrap">
                    Más popular
                  </span>
                )}
                <div className="mb-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-text-dim mb-1">
                    {PLAN_TAGLINE[plan]}
                  </p>
                  <h3 className="font-display text-xl font-bold">{PLAN_LABEL[plan]}</h3>
                </div>
                <p className="font-display text-3xl font-extrabold tabular-nums">
                  {plan === 'FREE' ? 'Gratis' : formatMoney(price)}
                  {plan !== 'FREE' && (
                    <span className="text-sm font-normal text-text-dim">/mes</span>
                  )}
                </p>
                {plan === 'FREE' && (
                  <p className="text-xs text-text-dim mt-0.5">
                    {freeTrialDays} días, sin tarjeta
                  </p>
                )}
                <ul className="mt-4 space-y-2.5 flex-1">
                  {PLAN_FEATURES[plan].map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-text-dim">
                      <Check className="h-4 w-4 text-brand shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                <a
                  href={TELEGRAM_BOT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    'mt-5 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold transition',
                    isPopular
                      ? 'bg-brand text-black hover:brightness-110'
                      : 'bg-panel-elevated text-text hover:bg-border',
                  )}
                >
                  {plan === 'FREE' ? 'Empezar gratis' : 'Elegir plan'}
                  <ArrowRight className="h-3.5 w-3.5" />
                </a>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
